import { tWithKeyAttr as t } from "../../i18nHelper";
import contactUsStyles from "./contactUs.module.css";
import onlineSupportIcon from "../../images/online_support_icon.svg";
import techSupportIcon from "../../images/tech_support_icon.svg";
import { ChangeEvent, useEffect, useState } from "react";
import { IContactUsTheme } from "../../utils/categories";
import FaqItem from "../faqItem/faqItem";
import { useGetFaqsQuery } from "../../services/api/user.api";
import { useAppSelector } from "../../services/hooks";
import { userSelector } from "../../services/selectors/user.selectors";
import {
  auxiliaryWords,
  contactUsList,
  ContactUsSearchOptions,
  customerService,
} from "../../utils/constants";
import PrevencherAnswer from "../prevencherAnswer/prevencherAnswer";
import { useNavigate } from "react-router-dom";
import i18n from "../../i18n";

function ContactUs() {
  const navigate = useNavigate();

  const [searchOption, setSearchOption] = useState<ContactUsSearchOptions>(
    ContactUsSearchOptions.Input
  );
  const [value, setValue] = useState<string>("");
  const [toggle, setToggle] = useState({});
  const [variants, setVariants] = useState<IContactUsTheme[]>([]);
  const [showThemes, setShowThemes] = useState(false);
  const user = useAppSelector(userSelector);
  const companyId = user?.companyId;

  const { data: faqs = [], isSuccess } = useGetFaqsQuery(companyId, {
    skip: !companyId,
  });

  const activeFaqs = faqs
    .filter((faq) => faq.statusId === 1)
    .sort((a, b) => a.seq - b.seq);

  useEffect(() => {
    if (!value.length) {
      setVariants([]);
    } else {
      const words = value
        .split(" ")
        .filter((word) => !auxiliaryWords.includes(word));
      setVariants(
        // eslint-disable-next-line array-callback-return
        contactUsList.filter((d) => {
          for (let i = 0; i < words.length; i++) {
            if (t(d.text).includes(words[i]) || t(d.title).includes(words[i])) {
              return d;
            }
          }
        })
      );
    }
  }, [value]);

  const toggleAnswer = (id: number) => {
    setToggle({
      ...toggle,
      [id]: !toggle[id as keyof typeof setToggle],
    });
  };

  const onChangeValue = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchOption(ContactUsSearchOptions.Input);
    setShowThemes(false);
    setValue(event.target.value);
  };

  return (
    <section dir={i18n.dir()} className={contactUsStyles.section}>
      <h2 className={contactUsStyles.section__title}>{t('SEARCH_FOR_SOLUTION_HERE')}</h2>
      <ul className={contactUsStyles.searchOptions}>
        <li
          className={`${contactUsStyles.searchOption} ${
            searchOption === ContactUsSearchOptions.Input &&
            contactUsStyles.searchOption_active
          }`}
          onClick={() => setSearchOption(ContactUsSearchOptions.Input)}
        >
          {t('HOW_CAN_WE_HELP_YOU')}</li>
        <li
          className={`${contactUsStyles.searchOption} ${
            searchOption === ContactUsSearchOptions.List &&
            contactUsStyles.searchOption_active
          }`}
          onClick={() => {
            setShowThemes(false);
            setVariants([]);
            setValue("");
            setSearchOption(ContactUsSearchOptions.List);
          }}
        >
          {t('LIST_OF_TOPICS')}</li>
      </ul>
      <input
        type={"text"}
        name={"search"}
        id={"search"}
        onChange={onChangeValue}
        value={value}
        className={contactUsStyles.input}
      />
      {variants.length > 0 && !showThemes && (
        <ul className={contactUsStyles.variants}>
          {variants.map((v, index) => (
            <li
              key={index}
              className={contactUsStyles.variant}
              onClick={() => {
                setValue(t(v.title));
                setShowThemes(true);
              }}
            >
              {t(v.title)}
            </li>
          ))}
        </ul>
      )}
      {showThemes && (
        <>
          <ul
            className={`${contactUsStyles.questionList} ${contactUsStyles.questionList_prevencher}`}
          >
            {variants.map((theme, index) => (
              <PrevencherAnswer key={index} theme={theme} />
            ))}
          </ul>
        </>
      )}
      {searchOption === ContactUsSearchOptions.Input ? (
        <>
          <p className={contactUsStyles.text}>
            {t('IF_NO_SOLUTION_CONTACT_US')}</p>

          <ul className={contactUsStyles.contactOptions}>
            <li
              className={`${contactUsStyles.contactOption} ${contactUsStyles.contactOption_disabled}`}
            >
              <h3 className={contactUsStyles.contactOption__title}>
                {t('TECHNICAL_SUPPORT')}</h3>
              <img
                className={contactUsStyles.contactOption__img}
                src={techSupportIcon}
                alt={t('TECHNICAL_SUPPORT')}
              />
              <button
                className={`${contactUsStyles.contactOption__btn} hover`}
                onClick={() => {
                  navigate("/profile", {
                    replace: true,
                    state: { pathname: "techSupport" },
                  });
                }}
              >
                {t('OPENING_A_REQUEST')}</button>
            </li>
            <li className={`${contactUsStyles.contactOption} hover`}>
              <h3 className={contactUsStyles.contactOption__title}>
                {t('CUSTOMER_SERVICE')}</h3>
              <img
                className={contactUsStyles.contactOption__img}
                src={onlineSupportIcon}
                alt={t('CUSTOMER_SERVICE')}
              />
              <button
                disabled={true}
                className={`${contactUsStyles.contactOption__btn} ${contactUsStyles.contactOption__btn_disabled} hover`}
                onClick={() => {
                  navigate("/profile", {
                    replace: true,
                    state: { pathname: customerService },
                  });
                }}
              >
                {t('OPENING_A_REQUEST')}</button>
            </li>
          </ul>
        </>
      ) : (
        <>
          <h3
            className={`${contactUsStyles.section__title} ${contactUsStyles.section__title_list}`}
          >
            {t('LIST_OF_TOPICS_1')}</h3>
          <ul className={contactUsStyles.questionList}>
            {isSuccess &&
              activeFaqs.map((faq, index) => (
                <FaqItem
                  key={faq.id}
                  faq={faq}
                  index={index}
                  toggle={toggle}
                  toggleAnswer={toggleAnswer}
                  setToggle={setToggle}
                />
              ))}
          </ul>
        </>
      )}
      <article className={contactUsStyles.section__footer}>
        <h4 className={contactUsStyles.footerTitle}>{t('HOURS_OF_OPERATION')}</h4>
        <p className={contactUsStyles.footerText}>
          {t('A_H_BETWEEN_HOURS_18_00_10_00')}</p>
        <p className={contactUsStyles.footerText}>
          {t('INQUIRIES_RESPONSE_TIME')}</p>
        <p className={contactUsStyles.footerText}>
          {t('INQUIRIES_AFTER_HOURS')}</p>
        <p className={contactUsStyles.footerText}>
          {t('URGENT_CASES_CALL')}</p>
      </article>
    </section>
  );
}

export default ContactUs;
